import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { useConfirm } from "material-ui-confirm";

import { AddServiceInput, ModelType } from "../../../API";
import ConfigurationComponent from "../../../common/components/box/ConfigurationComponent";
import FullPageLoader from "../../../common/components/item/FullPageLoader";
import ItemDetailHeader from "../../../common/components/item/ItemDetailHeader";
import ItemDetailTable from "../../../common/components/item/ItemDetailTable";
import MainPaperWrapper from "../../../common/components/item/MainPaperWrapper";
import ResponsiveSplitContainer from "../../../common/components/item/ResponsiveSplitContainer";
import LiveViewContainer, {
  ViewMode,
  ViewModeEnum,
} from "../../../common/components/live-view/LiveViewContainer";
import NodeSelect from "../../../common/components/select/NodeSelect/NodeSelect";
import BreadcrumbNavigation from "../../../common/components/tabs/BreadcrumbNavigation";
import confirmDialogStyleOptions from "../../../common/helpers/confirmDialogParams";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { useStreamProcesses } from "../../../common/hooks/useStreamProcesses";
import StyledLoadingButton from "../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import { useCreateService } from "../../model-manager/hooks/useCreateService";
import { useSyncShadow } from "../../model-manager/hooks/useSyncShadow";
import {
  ServiceTypeEnum,
  configurationParametersByModelType,
} from "../../model-manager/variables/modelManager";
import { IDeviceTableRow } from "../hooks/useDeviceTableRows";
import useGetDeviceById from "../hooks/useGetDeviceByUrl";
import { DEFAULT_MEDIA_HEIGHT } from "../../../common/variables/common";

const AddModelPage = (): JSX.Element => {
  const { modelToRun = "" } = useParams();

  const [deviceDetails, setDeviceDetails] = useState<IDeviceTableRow | null>(
    null
  );

  const [selectedNode, setSelectedNode] = useState(deviceDetails?.node.id);

  const { startStreaming, stopStreaming } = useStreamProcesses();

  const [currentViewMode, setCurrentViewMode] = useState<ViewMode>(
    ViewModeEnum.KEYFRAME
  );

  const { syncShadowsForNode, loading: syncShadowsLoading } = useSyncShadow();

  const { fetchDevice, getServicesLoading, isServiceLoaded, loading } =
    useGetDeviceById();

  const customerId = useCustomerIdGuard();

  const { createService, loading: createServiceLoading } = useCreateService();

  const navigate = useNavigate();

  const confirm = useConfirm();

  const deviceDetailPageLink = `/device/${encodeURIComponent(
    deviceDetails?.rowId ?? ""
  )}`;

  let correctConfigurations = configurationParametersByModelType[modelToRun];
  let currentModel = "";
  let dockerModelName = modelToRun; // correct are [gasleak | firesmoke | hardhat | gateguard | tanklevel | ss]

  switch (modelToRun) {
    case ModelType.ss:
      currentModel = ServiceTypeEnum.PERSONNEL_DETECTION;

      dockerModelName = ModelType.security;
      break;
    case ModelType.gasleak:
      currentModel = ServiceTypeEnum.LEAK;

      dockerModelName = ModelType.gasleak;
      break;

    case ModelType.liquidleak:
      currentModel = ServiceTypeEnum.LIQUID_LEAK;

      dockerModelName = ModelType.liquidleak;
      break;

    case ModelType.agg:
      currentModel = ServiceTypeEnum.GATE_GUARD;

      dockerModelName = "gateguard";
      break;

    case ModelType.fire:
    case ModelType.smoke:
    case ModelType.firesmoke:
      currentModel = ServiceTypeEnum.FIRE_SMOKE;

      dockerModelName = ModelType.firesmoke;
      break;

    case ModelType.hardhat:
      currentModel = ServiceTypeEnum.HARD_HAT;

      dockerModelName = ModelType.hardhat;
      break;

    case ModelType.tlm:
      currentModel = ServiceTypeEnum.TANK_LEVEL;

      dockerModelName = "tanklevel";
      break;

    default:
      navigate(deviceDetailPageLink);
      break;
  }

  correctConfigurations = {
    docker_image: `534256877503.dkr.ecr.us-east-1.amazonaws.com/ccai-${dockerModelName}:latest`,
    ...(correctConfigurations as object),
  };

  const [configurationParams, setConfigurationParams] = useState({
    editMode: false,
    value: JSON.stringify(correctConfigurations),
  });

  useEffect((): void => {
    if (!isServiceLoaded) return;

    const getDevice = async (): Promise<IDeviceTableRow | null> => {
      try {
        const device = await fetchDevice();

        if (!device) {
          return null;
        }

        setCurrentViewMode(ViewModeEnum.KEYFRAME);

        setDeviceDetails(device);

        setSelectedNode(
          device.node.id?.startsWith("C_")
            ? device.node.id
                .replace("C_", "C#")
                .replace("_L_", "#L#")
                .replace("_N_", "#N#")
            : device.node.id
        );

        return device;
      } catch (error) {
        errorNotification("Failed to fetch device details");

        console.error(error);

        return null;
      }
    };

    getDevice().then((device): void => {
      if (device) {
        startStreaming({
          deviceId: device.rowId,
          nodeId: device.node.id,
          hasPanTilt: device.deviceData?.hasPanTilt,
        });
      }
    });
  }, [isServiceLoaded]);

  useEffect((): void => {
    if (deviceDetails) {
      stopStreaming({
        deviceId: deviceDetails.rowId,
        nodeId: deviceDetails.node.id,
        hasPanTilt: deviceDetails.deviceData?.hasPanTilt,
      });
    }
  }, [deviceDetails]);

  useEffect((): void => {
    const modelIsAssigned = deviceDetails?.models.find(
      (model): boolean => model.serviceName === currentModel
    );

    if (modelIsAssigned) {
      navigate(deviceDetailPageLink);
    }
  }, [deviceDetails]);

  const memoizedSetCurrentViewModeCallback = useCallback((mode: ViewMode) => {
    setCurrentViewMode(mode);
  }, []);

  const keyFrameOrRecentFrame = useMemo(() => {
    if (deviceDetails?.deviceData.hasPanTilt) {
      return deviceDetails.deviceData.signedUrlRecentFrame ?? "";
    }

    return deviceDetails?.deviceData.signedUrlKeyFrame ?? "";
  }, [deviceDetails]);

  const confirmCancel = (): void => {
    confirm({
      title: "Are you sure you want to cancel model adding?",
      confirmationText: "Confirm",
      cancellationText: "Cancel",
      ...confirmDialogStyleOptions,
      confirmationButtonProps: {
        ...confirmDialogStyleOptions.confirmationButtonProps,
        disabled: loading,
        color: "primary",
      },
    }).then((): void => {
      navigate(deviceDetailPageLink);
    });
  };

  const onAddModelHandler = (): void => {
    const input: Omit<AddServiceInput, "timeZone"> = {
      serviceType:
        currentModel === ServiceTypeEnum.LEAK
          ? ServiceTypeEnum.GAS_LEAK
          : currentModel,
      nodeId: selectedNode ?? "",
      deviceId: deviceDetails?.rowId ?? "",
      locationId: deviceDetails?.location.id ?? "",
      customerId,
      configuration: configurationParams.value,
    };

    createService(input)
      .then(response => {
        if (response?.data?.addService) {
          const service = response.data.addService;

          return service;
        }

        if (response.errors) {
          errorNotification();
        }
      })
      .then(service => {
        if (!service) return;

        service.nodeId && syncShadowsForNode(service.nodeId);

        successNotification(`${currentModel} model is added`);

        navigate(
          `${deviceDetailPageLink}/model/${encodeURIComponent(currentModel)}`
        );
      })
      .catch((error): void => {
        errorNotification();

        console.error(error);
      });
  };

  const cancelConfigurationEdit = (): void => {
    setConfigurationParams({ ...configurationParams, editMode: false });
  };

  const saveConfigurationEdit = (value: string): void => {
    setConfigurationParams({ value, editMode: false });
  };

  const memoizedOnNodeChangeHandler = useCallback((value: string) => {
    setSelectedNode(value);
  }, []);

  const tableData = [
    {
      label: "CONFIGURATION PARAMETERS",
      value: configurationParams.value,
      jsonFormat: true,
      onEditHandler: (): void => {
        setConfigurationParams({ ...configurationParams, editMode: true });
      },
    },
  ];

  const breadcrumbItems = [
    { label: "Devices", path: "/devices" },
    {
      label: deviceDetails?.name ?? "",
      path: "/device/" + encodeURIComponent(deviceDetails?.rowId ?? ""),
    },
    { label: `Add ${currentModel.toLowerCase()} model` },
  ];

  const memoizedData = useMemo(() => {
    return [
      {
        label: "NODE",
        labelStyles: {
          display: "flex",
          alignItems: "center",
          padding: 0,
        },
        value: (
          <NodeSelect
            label=""
            nodeId={selectedNode}
            onNodeChange={memoizedOnNodeChangeHandler}
          />
        ),
      },
    ];
  }, [selectedNode]);

  if (loading || getServicesLoading || !deviceDetails) {
    return <FullPageLoader />;
  }

  return (
    <>
      <BreadcrumbNavigation items={breadcrumbItems} />
      <ItemDetailHeader name={`Add ${currentModel.toLowerCase()} model`} />

      <MainPaperWrapper>
        <ResponsiveSplitContainer gap="1.5em">
          <Box sx={{ width: "100%" }}>
            <ItemDetailTable data={memoizedData} />

            <Divider sx={{ margin: "0.5em 0", opacity: "0.5" }} />

            {!configurationParams.editMode && (
              <ItemDetailTable data={tableData} />
            )}

            {configurationParams.editMode && (
              <ConfigurationComponent
                model={modelToRun}
                onCancelHandler={cancelConfigurationEdit}
                onSaveHandler={saveConfigurationEdit}
                params={configurationParams.value}
              />
            )}
          </Box>

          <Box>
            <Box
              sx={{
                height: "100%",
              }}
            >
              <LiveViewContainer
                s3Key={keyFrameOrRecentFrame}
                showPtButton={deviceDetails?.deviceData?.hasPanTilt}
                fixedHeight={DEFAULT_MEDIA_HEIGHT}
                device={deviceDetails}
                viewMode={currentViewMode}
                setCurrentViewMode={memoizedSetCurrentViewModeCallback}
                defaultViewMode={ViewModeEnum.KEYFRAME}
              />
            </Box>
          </Box>
        </ResponsiveSplitContainer>
      </MainPaperWrapper>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "1em",
        }}
      >
        <StyledLoadingButton
          sx={{
            marginTop: "1.5em",
          }}
          loading={false}
          variant="outlined"
          color="inherit"
          onClick={(): void => confirmCancel()}
        >
          Back
        </StyledLoadingButton>

        <StyledLoadingButton
          sx={{
            marginTop: "1.5em",
          }}
          loading={createServiceLoading || syncShadowsLoading}
          variant="contained"
          color="primary"
          onClick={onAddModelHandler}
        >
          Add model
        </StyledLoadingButton>
      </Box>
    </>
  );
};

export default AddModelPage;
